import { Copy, ExternalLink, Shield, Landmark, Vote } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/shared";

const AUR = {
  tokenName: "Aurva", symbol: "AUR",
  contract: "0x7CbE4661Bd8BC385Fa9E35FC0e625A3fbE8D53a5",
  blockchain: "BNB Smart Chain (BSC)", network: "BNB Smart Chain",
  standard: "BEP-20", decimals: 18, maxSupply: "1,000,000,000",
  circulating: "To Be Announced (TBA)", assetType: "Utility Token",
  inflation: "Fixed Supply",
  bscscan: "https://bscscan.com/token/0x7CbE4661Bd8BC385Fa9E35FC0e625A3fbE8D53a5",
};

const OVERVIEW = [
  ["Token Name", AUR.tokenName], ["Ticker", AUR.symbol],
  ["Blockchain", AUR.blockchain], ["Network", AUR.network],
  ["Token Standard", AUR.standard], ["Decimals", String(AUR.decimals)],
  ["Maximum Total Supply", `${AUR.maxSupply} AUR`], ["Circulating Supply", AUR.circulating],
  ["Asset Type", AUR.assetType], ["Inflation Model", AUR.inflation],
];

const UTILITIES = [
  "Platform transaction fees", "Access to tokenized Real World Asset offerings",
  "Staking and ecosystem rewards", "Governance participation",
  "Reduced platform service fees", "Institutional membership benefits",
  "Liquidity incentives", "Partner ecosystem integrations", "Premium platform access",
];

const ALLOCATION = [
  "Ecosystem Development", "Treasury Reserve", "Community Incentives",
  "Strategic Partnerships", "Team & Advisors", "Liquidity",
  "Marketing & Growth", "Institutional Development",
];

const SECURITY = {
  "Security Foundations": [
    "Independently audited smart contracts", "Multi-signature treasury management",
    "Secure on-chain transactions", "Continuous security monitoring",
    "Enterprise security best practices",
  ],
  "Institutional Integration": [
    "Institutional-grade custody compatibility",
    "Designed for compliant RWA tokenization flows",
    "Governance-aligned incentives for long-term participants",
  ],
};

export default function AurToken({ onCta }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(AUR.contract); toast.success("Contract address copied"); }
    catch { toast.error("Unable to copy — please copy manually."); }
  };

  return (
    <>
      <Navbar onCta={onCta} />
      <main className="pt-36 pb-24">
        <div className="container-auvra">
          {/* Hero */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
            <div>
              <Reveal><span className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-xs tracking-[0.2em] uppercase text-white/80 mb-6">BNB Smart Chain · BEP-20 · RWA Utility</span></Reveal>
              <Reveal i={1}><h1 className="text-5xl md:text-7xl font-semibold tracking-tighter">{AUR.tokenName} <span className="text-auvra-blue">({AUR.symbol})</span></h1></Reveal>
              <Reveal i={2}><p className="text-lg text-secondary-auvra mt-6 leading-relaxed max-w-xl">The native utility token powering the AUVRA Real World Asset platform on {AUR.blockchain} — enabling secure, efficient, cost-effective participation across a compliant tokenization ecosystem.</p></Reveal>
              <Reveal i={3} className="mt-8">
                <div className="kicker mb-2">Contract Address</div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <code data-testid="aur-contract" className="flex-1 truncate rounded-xl border border-white/10 bg-black px-4 py-3 text-xs font-mono text-auvra-blue">{AUR.contract}</code>
                  <button onClick={copy} data-testid="copy-contract-btn" className="flex items-center justify-center gap-2 border border-white/15 hover:border-auvra-blue rounded-xl px-4 py-3 text-sm transition-colors"><Copy className="w-4 h-4" /> Copy</button>
                </div>
              </Reveal>
              <Reveal i={4} className="flex flex-wrap gap-3 mt-6">
                <a href={AUR.bscscan} target="_blank" rel="noopener noreferrer" data-testid="bscscan-link" className="flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">View on BscScan <ExternalLink className="w-4 h-4" /></a>
                <button onClick={onCta} className="border border-white/15 hover:border-white/40 text-white font-medium px-6 py-3 rounded-full transition-all hover:bg-white/5">Trade AUR</button>
              </Reveal>
            </div>
            <Reveal i={2}>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-sm font-medium text-secondary-auvra mb-6 uppercase tracking-wider">Token Overview</h2>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-5">
                  {OVERVIEW.map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-xs text-secondary-auvra">{k}</dt>
                      <dd className="font-medium mt-1">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Reveal>
          </div>

          {/* Utilities */}
          <Section title="Core Utilities" sub="AUR is the utility backbone of the AUVRA RWA platform — enabling on-chain value transfer, governance, incentives and access across compliant tokenization flows.">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {UTILITIES.map((u, i) => (
                <Reveal key={u} i={i % 3}><div className="glass rounded-xl px-5 py-4 text-sm"><span className="text-auvra-blue mr-2">•</span>{u}</div></Reveal>
              ))}
            </div>
          </Section>

          {/* Tokenomics */}
          <Section title="Tokenomics" sub={`Aurva (AUR) has a fixed maximum supply of ${AUR.maxSupply} AUR, designed for long-term ecosystem alignment and sustainable platform growth.`}>
            <Reveal>
              <div className="glass rounded-2xl p-8">
                <h3 className="text-sm font-medium mb-5 uppercase tracking-wider text-secondary-auvra">Allocation Categories</h3>
                <ul className="grid gap-3 md:grid-cols-2">
                  {ALLOCATION.map((a) => (
                    <li key={a} className="flex items-start gap-3"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-auvra-blue flex-shrink-0" />{a}</li>
                  ))}
                </ul>
                <p className="mt-5 text-xs text-secondary-auvra">Exact percentage allocations and vesting schedules are published in the Aurva tokenomics documentation and governance proposals.</p>
              </div>
            </Reveal>
          </Section>

          {/* Security */}
          <Section title="Security & Infrastructure" sub="AUR integrates with institutional-grade infrastructure while leveraging robust on-chain security practices across the AUVRA ecosystem.">
            <div className="grid gap-6 md:grid-cols-2">
              {Object.entries(SECURITY).map(([title, items], i) => (
                <Reveal key={title} i={i}>
                  <div className="glass rounded-2xl p-8 h-full">
                    {i === 0 ? <Shield className="w-6 h-6 text-auvra-blue mb-4" /> : <Landmark className="w-6 h-6 text-auvra-blue mb-4" />}
                    <h3 className="text-sm font-medium mb-4 uppercase tracking-wider text-secondary-auvra">{title}</h3>
                    <ul className="space-y-2 text-sm text-white/90">{items.map((it) => <li key={it}>• {it}</li>)}</ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </Section>

          {/* Governance + Vision */}
          <Section title="Governance & Vision">
            <div className="grid gap-6 md:grid-cols-2">
              <Reveal><div className="glass rounded-2xl p-8 h-full"><Vote className="w-6 h-6 text-auvra-blue mb-4" /><h3 className="text-lg font-medium mb-3">Governance</h3><p className="text-secondary-auvra text-sm leading-relaxed">AUR holders participate in governance across the AUVRA ecosystem — protocol enhancements, platform upgrades, ecosystem incentives and strategic decisions — aligning incentives with the long-term, regulatory-aligned evolution of the platform.</p></div></Reveal>
              <Reveal i={1}><div className="glass rounded-2xl p-8 h-full"><h3 className="text-lg font-medium mb-3">Vision</h3><p className="text-secondary-auvra text-sm leading-relaxed">Aurva (AUR) serves as the utility layer of AUVRA, enabling compliant Real World Asset tokenization and unlocking secure, transparent participation in a global, blockchain-powered investment infrastructure on BNB Smart Chain — bridging institutional standards with on-chain efficiency.</p></div></Reveal>
            </div>
          </Section>

          {/* Disclaimer */}
          <div className="border-t border-white/10 pt-8 mt-16">
            <h3 className="text-sm font-medium mb-2">Disclaimer</h3>
            <p className="text-xs text-secondary-auvra leading-relaxed max-w-4xl">Aurva (AUR) is a utility token designed exclusively for use within the AUVRA RWA ecosystem. It does not represent equity, debt, ownership, or any claim to profits in AUVRA. Nothing herein constitutes investment, legal, tax or financial advice. Access to and use of AUR may be restricted in certain jurisdictions and is subject to applicable laws and regulatory requirements.</p>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function Section({ title, sub, children }) {
  return (
    <section className="mb-20">
      <Reveal><h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3">{title}</h2></Reveal>
      {sub && <Reveal i={1}><p className="text-secondary-auvra max-w-2xl mb-8 leading-relaxed">{sub}</p></Reveal>}
      {children}
    </section>
  );
}
